import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import langStrings from '../i18n';

const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const DRAFT_PATH_PREFIX = '/draft-details';
const RELOAD_FLAG_KEY = 'versionReloadAttempted';

const isOnDraftPage = () =>
  window.location.pathname.startsWith(DRAFT_PATH_PREFIX);

// Appending a fresh query param guarantees a cache miss for the top-level
// document, unlike location.reload(), which some browsers can still satisfy
// from disk cache when the stale response has no validators.
const reloadWithFreshCache = () => {
  const url = new URL(window.location.href);
  url.searchParams.set('_v', Date.now().toString());
  window.location.replace(url.toString());
};

const fetchLatestVersion = async () => {
  const response = await fetch(`/meta.json?_=${Date.now()}`, {
    cache: 'no-store',
  });
  if (!response.ok) return null;
  const { version } = await response.json();
  return version;
};

// Detects when a newer build has been deployed while this tab is still
// running an old, browser-cached bundle, and forces a cache-busting reload
// to pick it up. Deferred while a live draft is in progress so an in-flight
// pick isn't interrupted.
const VersionChecker = () => {
  const location = useLocation();
  const pendingVersionRef = useRef(null);

  const attemptReload = (latestVersion) => {
    if (sessionStorage.getItem(RELOAD_FLAG_KEY) === latestVersion) {
      // Already reloaded once for this version; avoid a reload loop and
      // just ask the user to refresh manually.
      toast.info(langStrings.NewAppVersion, { autoClose: false });
      return;
    }
    sessionStorage.setItem(RELOAD_FLAG_KEY, latestVersion);
    reloadWithFreshCache();
  };

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') return undefined;
    const currentVersion = process.env.REACT_APP_VERSION;
    if (!currentVersion) return undefined;

    const checkVersion = async () => {
      try {
        const latestVersion = await fetchLatestVersion();
        if (!latestVersion || latestVersion === currentVersion) return;

        if (isOnDraftPage()) {
          if (!pendingVersionRef.current) {
            pendingVersionRef.current = latestVersion;
            toast.info(langStrings.NewAppVersion, { autoClose: false });
          }
          return;
        }

        attemptReload(latestVersion);
      } catch (error) {
        // Transient network error; retry on the next interval.
      }
    };

    checkVersion();
    const interval = setInterval(checkVersion, CHECK_INTERVAL_MS);
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') checkVersion();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (pendingVersionRef.current && !isOnDraftPage()) {
      attemptReload(pendingVersionRef.current);
      pendingVersionRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return null;
};

export default VersionChecker;
